/* Disaster Recovery Drill — guided restore walkthrough (CEO only). */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  LifeBuoy, CheckCircle2, Circle, Mail, Loader2, ShieldCheck,
  HardDriveDownload, KeyRound, CalendarClock,
} from 'lucide-react';

const NAVY = '#1B2A4A';
const GOLD = '#B88C40';

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

function ReadyTile({ icon: Icon, label, value, ok }) {
  return (
    <div className="flex-1 min-w-[180px] bg-white border rounded-xl px-4 py-3"
      style={{ borderColor: ok ? '#B8D8C2' : '#F3C6C6' }}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest" style={{ color: ok ? '#1E6B45' : '#A32D2D' }}>
        <Icon size={12} /> {label}
      </div>
      <div className="text-[13px] font-semibold mt-1" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}

export default function DisasterRecoveryDrill() {
  const [data, setData] = useState(null);
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dr_drill_done') || '{}'); } catch { return {}; }
  });
  const [mailBusy, setMailBusy] = useState(false);

  useEffect(() => {
    api.get('/admin/recovery-drill').then(r => setData(r.data)).catch(() => {});
  }, []);

  const toggle = (n) => {
    const next = { ...done, [n]: !done[n] };
    setDone(next);
    localStorage.setItem('dr_drill_done', JSON.stringify(next));
  };

  const emailMe = async () => {
    setMailBusy(true);
    try {
      const r = await api.post('/admin/recovery-drill/email');
      toast.success(`Guide emailed to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed');
    } finally { setMailBusy(false); }
  };

  const r = data?.readiness;
  const completed = data ? data.steps.filter(s => done[s.n]).length : 0;

  return (
    <div className="p-8 max-w-4xl space-y-5" data-testid="recovery-drill-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2" style={{ color: NAVY, fontFamily: 'Fraunces, serif' }}>
            <LifeBuoy size={26} color={GOLD} /> Disaster Recovery Drill
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-1 max-w-xl">
            If Emergent vanished tomorrow, this walkthrough rebuilds ScaleBoard from your
            Google Drive DB dump + the encrypted env zip in ~45-60 minutes. Run it as a
            drill twice a year so it's muscle memory.
          </p>
        </div>
        <button onClick={emailMe} disabled={mailBusy} data-testid="drill-email-button"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: NAVY }}>
          {mailBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {mailBusy ? 'Sending…' : 'Email me this guide'}
        </button>
      </div>

      {r && (
        <div className="flex flex-wrap gap-3" data-testid="drill-readiness">
          <ReadyTile icon={HardDriveDownload} label="Drive DB backup" ok={!!r.last_db_backup}
            value={r.last_db_backup ? `${fmt(r.last_db_backup.at)} · ${r.last_db_backup.mb} MB` : 'None found'} />
          <ReadyTile icon={KeyRound} label="Env snapshot emailed" ok={!!r.last_env_snapshot}
            value={r.last_env_snapshot ? `${fmt(r.last_env_snapshot.at)} → ${r.last_env_snapshot.to}` : 'Never sent'} />
          <ReadyTile icon={CalendarClock} label="Monthly auto snapshot" ok={r.monthly_snapshot_armed}
            value={r.monthly_snapshot_armed ? 'Armed · 1st, 07:00 IST' : 'Not configured'} />
          <ReadyTile icon={ShieldCheck} label="Local snapshot" ok={!!r.local_snapshot}
            value={r.local_snapshot ? `${fmt(r.local_snapshot.at)} · ${r.local_snapshot.mb} MB` : 'None'} />
        </div>
      )}

      {data && (
        <div className="text-[12px] font-semibold" style={{ color: completed === data.steps.length ? '#1E6B45' : '#8A7B5C' }} data-testid="drill-progress">
          Drill progress: {completed}/{data.steps.length} steps
          {completed === data.steps.length ? ' — you are recovery-ready 🎉' : ''}
        </div>
      )}

      {!data ? (
        <div className="text-sm text-[#9CA3AF]">Loading…</div>
      ) : data.steps.map((s) => (
        <div key={s.n} className="bg-white border rounded-xl overflow-hidden"
          style={{ borderColor: done[s.n] ? '#B8D8C2' : '#E5E1DC' }} data-testid={`drill-step-${s.n}`}>
          <button onClick={() => toggle(s.n)} data-testid={`drill-step-toggle-${s.n}`}
            className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-[#FBF9F5]">
            {done[s.n]
              ? <CheckCircle2 size={20} style={{ color: '#2D8B5E', flexShrink: 0 }} />
              : <Circle size={20} style={{ color: '#C9C1AE', flexShrink: 0 }} />}
            <div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: GOLD }}>Step {s.n}</div>
              <div className="text-[15px] font-bold" style={{ color: NAVY, textDecoration: done[s.n] ? 'line-through' : 'none' }}>
                {s.title}
              </div>
            </div>
          </button>
          <div className="px-5 pb-4 pl-[52px]">
            <p className="text-[12px] italic mb-2" style={{ color: '#8A7B5C' }}>{s.goal}</p>
            <ol className="list-decimal pl-4 space-y-1.5">
              {s.actions.map((a, i) => (
                <li key={i} className="text-[13px]" style={{ color: '#3A3A3A' }}>{a}</li>
              ))}
            </ol>
            <div className="mt-2.5 text-[12px] font-semibold rounded-lg px-3 py-1.5 inline-block"
              style={{ background: '#F0F7F2', color: '#1E6B45' }}>
              ✔ Done when: {s.check}
            </div>
          </div>
        </div>
      ))}

      <p className="text-[11px] text-[#9CA3AF]">
        The destructive restore endpoint requires the exact phrase <b>RESTORE-SCALEBOARD</b> — it can never fire by accident.
      </p>
    </div>
  );
}
